import { openDB, type IDBPDatabase } from 'idb'
import type { Generation } from './api'

interface FrankenvibesDB {
  generations: {
    key: string
    value: Generation
    indexes: { 'by-date': string }
  }
  blobs: {
    key: string
    value: { id: string; data: Blob; contentType: string }
  }
}

let dbPromise: Promise<IDBPDatabase<FrankenvibesDB>> | null = null

export function getDB(): Promise<IDBPDatabase<FrankenvibesDB>> {
  if (!dbPromise) {
    dbPromise = openDB<FrankenvibesDB>('frankenvibes-lab', 1, {
      upgrade(db) {
        const generationsStore = db.createObjectStore('generations', {
          keyPath: 'id',
        })
        generationsStore.createIndex('by-date', 'created_at')
        db.createObjectStore('blobs', { keyPath: 'id' })
      },
    })
  }
  return dbPromise
}

export async function getCachedGenerations(): Promise<Generation[]> {
  const db = await getDB()
  const all = await db.getAllFromIndex('generations', 'by-date')
  return all.reverse()
}

export async function cacheGeneration(generation: Generation): Promise<void> {
  try {
    const db = await getDB()
    await db.put('generations', generation)
  } catch (err) {
    if (isQuotaExceeded(err)) {
      await clearOldCache(50)
      const db = await getDB()
      await db.put('generations', generation)
    }
  }
}

export async function cacheBlob(
  id: string,
  data: Blob,
  contentType: string,
): Promise<void> {
  try {
    const db = await getDB()
    await db.put('blobs', { id, data, contentType })
  } catch (err) {
    if (isQuotaExceeded(err)) {
      await clearOldCache(50)
      const db = await getDB()
      await db.put('blobs', { id, data, contentType })
    }
  }
}

export async function getCachedBlob(
  id: string,
): Promise<{ id: string; data: Blob; contentType: string } | undefined> {
  const db = await getDB()
  return db.get('blobs', id)
}

export async function deleteFromCache(id: string): Promise<void> {
  const db = await getDB()
  const tx = db.transaction(['generations', 'blobs'], 'readwrite')
  await Promise.all([
    tx.objectStore('generations').delete(id),
    tx.objectStore('blobs').delete(id),
    tx.done,
  ])
}

export async function clearOldCache(maxItems: number = 100): Promise<void> {
  const db = await getDB()
  const all = await db.getAllFromIndex('generations', 'by-date')

  if (all.length <= maxItems) return

  const toRemove = all.slice(0, all.length - maxItems)
  const tx = db.transaction(['generations', 'blobs'], 'readwrite')

  for (const gen of toRemove) {
    tx.objectStore('generations').delete(gen.id)
    tx.objectStore('blobs').delete(gen.id)
  }

  await tx.done
}

function isQuotaExceeded(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' || err.code === 22)
  )
}
